import { HttpStatusCode } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, lastValueFrom, map, Observable, of, Subject, tap, throwError } from 'rxjs';
import { retry } from 'rxjs/operators';
import { Tenant, TenantUpdate } from 'src/app/shared/models/tenant';
import { UserPermission } from 'src/app/shared/models/user-permission';
import { SaveResponse } from '../../models/save-response';
import { TenantRequest } from '../../models/tenant-request';
import { TenantResponse } from '../../models/tenant-response';
import { TenantUpdateRequest } from '../../models/tenant-update-request';
import { UserPermissionResponse } from '../../models/user-permission-response';
import { TenantApiService } from '../http/tenant-api.service';
import { ToastNotificationService } from '../toast-notification.service';
import AutoMapperService from './auto-mapper.service';

@Injectable({
    providedIn: 'root'
})
export class TenantService {
    private cachedTenants: Tenant[] | null = null;
    private cachedTenantsForAdmin: Tenant[] | null = null;
    private cachedUserPermissions: UserPermission[] | null = null;
    private cachedTenantsRequest: Promise<Tenant[]> | null = null;
    private cachedTenantsForAdminRequest: Promise<Tenant[]> | null = null;

    // broadcasts when tenant cache has cleared
    public get tenantCacheCleared$(): Observable<any> {
        return this.tenantCacheClearedSubject.asObservable();
    }
    private tenantCacheClearedSubject = new Subject<void>();

    constructor(
        private tenantApiService: TenantApiService,
        private toast: ToastNotificationService
    ) {}

    saveTenant(tenant: Tenant) {
        const tenantRequest = this.mapToTenantRequestV3(tenant);
        return this.tenantApiService.createTenant(tenantRequest).pipe(tap(() => this.clearCache()));
    }

    updateTenant(tenantId: string, tenant: TenantUpdate) {
        return this.tenantApiService.updateTenant(tenantId, tenant).pipe(
            map(
                data =>
                    ({
                        id: data.body?.id,
                        message: data.body?.message,
                        isSuccess: data.status === HttpStatusCode.Ok
                    }) as SaveResponse
            ),
            tap(() => this.clearCache())
        );
    }

    getTenant(tenantId: string) {
        return this.tenantApiService
            .getTenant(tenantId.toLocaleUpperCase())
            .pipe(map(response => AutoMapperService.mapToApplicationModel<TenantResponse, Tenant>(response)));
    }

    getUserPermissions(): Observable<UserPermission[]> {
        if (this.cachedUserPermissions) {
            return of(this.cachedUserPermissions);
        }
        return this.tenantApiService.getUserPermissions().pipe(
            map(response => {
                this.cachedUserPermissions =
                    response?.map(data =>
                        AutoMapperService.mapToApplicationModelSameCase<UserPermissionResponse, UserPermission>(data)
                    ) ?? [];
                return this.cachedUserPermissions;
            }),
            catchError(error => {
                this.clearCache();
                return throwError(() => error);
            })
        );
    }

    private async getCachedTenants(
        cache: Tenant[] | null,
        cacheRequest: Promise<Tenant[]> | null,
        apiCall: () => Observable<Tenant[]>,
        cacheSetter: (tenants: Tenant[]) => void,
        requestCacheSetter: (request: Promise<Tenant[]>) => void
    ): Promise<Array<Tenant>> {
        try {
            if (cache) {
                return Promise.resolve(cache);
            } else if (cacheRequest) {
                return cacheRequest;
            } else {
                const request = lastValueFrom(
                    apiCall().pipe(
                        retry({
                            count: 5,
                            delay: 2000,
                            resetOnSuccess: true
                        }),
                        catchError(() => {
                            this.toast.showError(
                                'Failed to load tenants after multiple attempts. Please contact an admin.',
                                'Failed to Load Tenants'
                            );
                            return of([]);
                        })
                    )
                );
                requestCacheSetter(request);
                const tenants = await request;
                cacheSetter(tenants);
                return Promise.resolve(tenants);
            }
        } catch (e) {
            this.clearCache();
            throw e;
        }
    }

    public getTenantsForAdmin(): Promise<Array<Tenant>> {
        return this.getCachedTenants(
            this.cachedTenantsForAdmin,
            this.cachedTenantsForAdminRequest,
            () =>
                this.tenantApiService
                    .getTenantsForAdmin()
                    .pipe(map(response => response?.map(data => <Tenant>data) ?? [])),
            tenants => (this.cachedTenantsForAdmin = tenants),
            request => (this.cachedTenantsForAdminRequest = request)
        );
    }

    public getTenants(): Promise<Array<Tenant>> {
        return this.getCachedTenants(
            this.cachedTenants,
            this.cachedTenantsRequest,
            () =>
                this.tenantApiService.getTenants().pipe(
                    map(response => {
                        return (
                            response?.map(data => {
                                const mappedData = AutoMapperService.mapToApplicationModel<TenantResponse, Tenant>(
                                    data
                                );
                                AutoMapperService.convertStringsToDates(mappedData, 'updatedTime');
                                return mappedData;
                            }) ?? []
                        );
                    })
                ),
            tenants => (this.cachedTenants = tenants),
            request => (this.cachedTenantsRequest = request)
        );
    }

    clearCache() {
        this.cachedTenants = null;
        this.cachedTenantsRequest = null;
        this.cachedTenantsForAdmin = null;
        this.cachedTenantsForAdminRequest = null;
        this.tenantCacheClearedSubject.next();
    }

    mapToTenantRequestV3(data: Tenant) {
        const { locationId, ...newData } = data;

        return {
            ...newData,
            azureLocationId: parseInt(data.locationId)
        } as TenantRequest;
    }
}
